import React from 'react';
import './dashboard.css';

const pageStyle = {
    height: '100vh',
    padding: '32px 20px 56px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    zIndex: 9999,
    backgroundColor: 'var(--ui-dashboard-bg, #050505)',
    color: 'var(--ui-text-primary, #f4f4f5)',
};

const contentStyle = {
    width: '100%',
    maxWidth: '1120px',
    margin: '0 auto',
    lineHeight: 1.65,
};

const titleStyle = {
    color: 'var(--ui-text-primary, #f4f4f5)',
    fontWeight: 700,
    letterSpacing: 0,
};

const sectionTitleStyle = {
    color: 'var(--ui-text-primary, #f4f4f5)',
    fontWeight: 700,
    letterSpacing: 0,
    marginTop: '28px',
};

const textStyle = {
    color: 'var(--ui-text-secondary, #c7c7c7)',
};

const listStyle = {
    ...textStyle,
    paddingLeft: '22px',
};

const dividerStyle = {
    borderColor: 'var(--ui-dashboard-border, #2a2a2a)',
    opacity: 1,
};

const mutedStyle = {
    fontSize: '0.8rem',
    marginTop: '40px',
    color: 'var(--ui-text-muted, #8f8f8f)',
};

const PrivacyPolicy = () => {
    return (
        <div style={pageStyle}>
          <div style={contentStyle}>
            <h4 className="mb-3" style={titleStyle}>Privacy Policy</h4>

            <p style={textStyle}>
                Provio Solution (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) takes your privacy seriously. This Privacy Policy describes the types of information we collect, how we use and protect it, your rights concerning your data, and how you can contact us with questions. Our Electronic Health Record (EHR) system is designed to improve hospital record management while maintaining the highest standards of data security and compliance.
            </p>

            <hr className="my-4" style={dividerStyle} />

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                1. Information We Collect
            </h3>
            <p style={textStyle}>We collect various types of information to provide and improve our services, including:</p>
            <ul style={listStyle}>
                <li><strong>Personal Identifiable Information:</strong> Your name, email address, phone number, role within your organization, and any other information you provide during registration or profile updates.</li>
                <li><strong>Health Data:</strong> Any medical records, diagnoses, treatment plans, imaging data, lab results, medication details, and other health-related information you enter or upload into the EHR system.</li>
                <li><strong>Device & Access Information:</strong> IP addresses, device identifiers, browser type, operating system, and other technical data collected automatically when you access the platform.</li>
                <li><strong>Usage Data:</strong> Information about how you interact with the platform including session duration, pages visited, features used, and errors encountered.</li>
                <li><strong>Cookies & Tracking Technologies:</strong> We use cookies and similar technologies to personalize your experience, analyze usage, and support security measures.</li>
            </ul>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                2. How We Use Your Information
            </h3>
            <p style={textStyle}>Your information helps us to:</p>
            <ul style={listStyle}>
                <li>Provide secure and personalized access to our EHR platform tailored to your role and preferences.</li>
                <li>Facilitate accurate and efficient hospital record management and medical workflows.</li>
                <li>Send you important notifications such as appointment reminders, policy updates, or system alerts.</li>
                <li>Improve our platform through data analytics and usage monitoring.</li>
                <li>Ensure compliance with applicable laws and regulations, including healthcare-specific requirements like HIPAA where relevant.</li>
                <li>Prevent fraud, unauthorized access, and other malicious activities.</li>
            </ul>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                3. Data Sharing and Disclosure
            </h3>
            <p style={textStyle}>We do not sell or rent your personal or health information. Data may be shared only in the following circumstances:</p>
            <ul style={listStyle}>
                <li>With authorized hospital staff and administrators strictly on a need-to-know basis to provide care and manage records.</li>
                <li>With trusted third-party service providers who assist with hosting, analytics, notification delivery, or other operational tasks, under strict confidentiality agreements.</li>
                <li>To comply with legal obligations such as court orders, government investigations, or reporting requirements.</li>
                <li>In emergency situations to protect your health or safety or that of others.</li>
                <li>If the business is merged, acquired, or sold, data may be transferred as part of the transaction with full privacy safeguards.</li>
            </ul>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                4. Data Security Measures
            </h3>
            <p style={textStyle}>
                Protecting your sensitive health information is our highest priority. We implement industry-leading security practices, including but not limited to:
            </p>
            <ul style={listStyle}>
                <li>Encryption of data in transit (using HTTPS/TLS) and at rest to prevent unauthorized access.</li>
                <li>Secure authentication mechanisms including multi-factor authentication (MFA) where available.</li>
                <li>Regular security audits, vulnerability assessments, and penetration testing.</li>
                <li>Role-based access controls ensuring users only see data relevant to their permissions.</li>
                <li>Continuous monitoring of our infrastructure and quick incident response protocols.</li>
                <li>Data backup and disaster recovery plans to prevent data loss.</li>
            </ul>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                5. Your Privacy Rights
            </h3>
            <p style={textStyle}>
                Depending on your jurisdiction, you may have the following rights regarding your personal and health information:
            </p>
            <ul style={listStyle}>
                <li>Access: Request copies of your data we hold.</li>
                <li>Correction: Request corrections to inaccurate or incomplete information.</li>
                <li>Deletion: Request removal of your personal data where legally permissible.</li>
                <li>Restriction: Limit how we use or share your data.</li>
                <li>Objection: Object to certain types of data processing such as direct marketing.</li>
                <li>Data Portability: Request your data in a structured, machine-readable format.</li>
                <li>Withdrawal of Consent: Withdraw any consent you have previously given.</li>
            </ul>
            <p style={textStyle}>
                To exercise these rights, please contact our Data Protection Officer at <strong>support@provio.health</strong>. We may require verification to protect your privacy.
            </p>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                6. Cookies and Tracking Technologies
            </h3>
            <p style={textStyle}>
                We use cookies to improve your experience, remember preferences, and analyze site traffic. You can manage your cookie preferences through your browser settings. Blocking certain cookies may affect platform functionality.
            </p>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                7. International Data Transfers
            </h3>
            <p style={textStyle}>
                Your information may be stored and processed in servers located in your country or internationally. When transferring data across borders, we ensure appropriate safeguards to maintain privacy and security as required by law.
            </p>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                8. Children’s Privacy
            </h3>
            <p style={textStyle}>
                Our platform is not intended for use by individuals under the age of 16. We do not knowingly collect personal data from children. If you believe we have inadvertently collected data from a minor, please contact us immediately to request deletion.
            </p>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                9. Changes to This Privacy Policy
            </h3>
            <p style={textStyle}>
                We may update this policy occasionally to reflect changes in regulations, technology, or our practices. Significant changes will be communicated through the platform or via email. Your continued use of our services after changes signifies your acceptance.
            </p>

            <h3 style={sectionTitleStyle} className="font-weight-bold text-sm">
                10. Contact Us
            </h3>
            <p style={textStyle}>
                If you have any questions, concerns, or requests related to this Privacy Policy or your data, please reach out to:
                <br />
                <strong>Email:</strong> support@provio
                <br />
                <strong>Address:</strong> 
                <br />
                <strong>Phone:</strong> +1 (555) 
            </p>

            <p style={mutedStyle}>
                Last updated: May 27, 2025
            </p>
          </div>
        </div>
    );
};

export default PrivacyPolicy;
